import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Maximize, Minimize, Volume2, VolumeX } from "lucide-react";
import { PublicWorkspacePayload } from "@shared/types";
import { collectMediaKeys } from "@shared/playlist";
import { api, ApiError } from "@/lib/api";
import { warmMediaCache } from "@/lib/offline";
import { useIdle } from "@/hooks/useIdle";
import { CrossfadePlayer } from "@/components/CrossfadePlayer";
import { ClockOverlay } from "@/components/ClockOverlay";
import { KioskErrorBoundary } from "@/components/KioskErrorBoundary";
import { cn } from "@/lib/utils";

export default function Screensaver() {
  const { workspace = "" } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<PublicWorkspacePayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [muted, setMuted] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);

  const lastPayloadRef = useRef<string>("");
  const load = useCallback(async () => {
    try {
      const payload = await api.publicScreensaver(workspace);
      setError(null);
      // Only update (re-render the player) when something actually changed, so
      // the 60s poll doesn't interrupt a playing video.
      const key = JSON.stringify(payload);
      if (key !== lastPayloadRef.current) {
        lastPayloadRef.current = key;
        setData(payload);
        // Pre-download media for offline playback (runs in the background).
        void warmMediaCache(collectMediaKeys(payload.config));
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load screensaver");
    }
  }, [workspace]);

  useEffect(() => {
    void load();
    // Poll for config changes so the kiosk picks up admin edits without reload.
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  // Kiosk hygiene: ask for persistent storage (so the offline media cache
  // can't be quota-evicted) and reload nightly around 3 AM. The reload
  // self-heals anything a 24/7 page accumulates (leaked player state, a
  // failed YouTube SDK load, memory growth) and picks up new deploys.
  useEffect(() => {
    void navigator.storage?.persist?.().catch(() => undefined);
    const next = new Date();
    next.setHours(3, Math.floor(Math.random() * 30), 0, 0); // 3:00–3:29 local
    if (next.getTime() <= Date.now()) next.setDate(next.getDate() + 1);
    const id = setTimeout(() => window.location.reload(), next.getTime() - Date.now());
    return () => clearTimeout(id);
  }, []);

  // If this was an old (renamed) URL, the server resolves it to the current
  // workspace — update the address bar to the canonical kiosk URL.
  useEffect(() => {
    if (data && data.workspace.slug && data.workspace.slug !== workspace) {
      navigate(`/screensaver/${data.workspace.slug}`, { replace: true });
    }
  }, [data, workspace, navigate]);

  const idleTimeoutMs = (data?.config.settings.idleTimeoutSec ?? 60) * 1000;
  const { idle } = useIdle({ timeoutMs: idleTimeoutMs, enabled: !!data });

  useEffect(() => {
    const onChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void document.documentElement.requestFullscreen().catch(() => undefined);
  }, []);

  // Only surface an error when there's nothing to play. If a 60s poll fails
  // while a playlist is already loaded, keep playing it — a network blip must
  // never replace a working waiting-room screen with an error card.
  if (error && !data) {
    return (
      <div className="kiosk-root show-cursor flex items-center justify-center text-white">
        <div className="text-center">
          <p className="text-2xl font-semibold">Screensaver unavailable</p>
          <p className="mt-2 text-white/60">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="kiosk-root flex items-center justify-center text-white/40">Loading…</div>
    );
  }

  // When idle, the chrome disappears and the playlist fully takes over.
  const chromeVisible = !idle;

  return (
    <div className={cn("kiosk-root", chromeVisible && "show-cursor")}>
      <KioskErrorBoundary>
        <CrossfadePlayer config={data.config} muted={muted} keyboard className="absolute inset-0" />
        <ClockOverlay config={data.config.settings.clockOverlay} />
      </KioskErrorBoundary>

      <div
        className={cn(
          "pointer-events-none absolute inset-x-0 top-0 z-10 flex items-center justify-between p-4 transition-opacity duration-500",
          chromeVisible ? "opacity-100" : "opacity-0",
        )}
      >
        <div className="rounded-md bg-black/40 px-3 py-1.5 text-xs text-white/70 backdrop-blur">
          ← → slides · ↑ ↓ groups · space = next
        </div>
        <div className="pointer-events-auto flex gap-2">
          <button
            type="button"
            aria-label={muted ? "Unmute" : "Mute"}
            onClick={() => setMuted((m) => !m)}
            className="rounded-md bg-black/50 p-2 text-white backdrop-blur transition hover:bg-black/70"
          >
            {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
          </button>
          <button
            type="button"
            aria-label={fullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            onClick={toggleFullscreen}
            className="rounded-md bg-black/50 p-2 text-white backdrop-blur transition hover:bg-black/70"
          >
            {fullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
